import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { C, card, label, btn, input, applyTheme, currentTheme, GLYPH, PLANET_GLYPH,
  ELEMENT_COLOR } from "../lib/theme";
import ChartWheel from "../lib/ChartWheel";

/* Session prep for a client chart, in two renderings:
   /clients/:chartId        — working view: highlights, timeline, typed notes
   /clients/:chartId/print  — same data in a print-optimized layout (light theme is
                              forced while mounted; browser print → PDF). */

const QUALITY_COLOR = () => ({ good: C.earth, bad: C.rose, challenging: C.fire,
  milestone: C.brass, neutral: C.dim });
const PREP_LOTS = ["Spirit", "Fortune", "Eros"];
const sign = (s) => <span style={{ color: ELEMENT_COLOR(s) }}>{GLYPH[s]} {s}</span>;

export default function ClientPrep({ print = false }) {
  const { chartId } = useParams();
  const [prep, setPrep] = useState(null);
  const [returns, setReturns] = useState([]);
  const [notes, setNotes] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [, tick] = useState(0);

  useEffect(() => {
    api.sessionPrep(chartId).then((p) => {
      setPrep(p); setNotes(p.chart.pro_notes || "");
      const to = new Date(Date.now() + p.horizon_days * 86400000)
        .toISOString().slice(0, 10);
      api.returns(chartId, p.as_of, to)
        .then((r) => setReturns(r.returns || [])).catch(() => {});
    }).catch((e) => setErr(e.message));
  }, [chartId]);

  // print rendering borrows the bright theme so ink wins over atmosphere
  useEffect(() => {
    if (!print) return;
    const prev = currentTheme();
    applyTheme("meridian"); tick((t) => t + 1);
    return () => { applyTheme(prev); };
  }, [print]);

  if (err) return <div style={{ color: C.rose }}>{err}</div>;
  if (!prep) return <div style={{ color: C.faint }}>Assembling session prep…</div>;

  const c = prep.chart;
  const qc = QUALITY_COLOR();
  const notable = Object.entries(prep.tally)
    .filter(([, t]) => t.total > 0 && (t.peak > 0 || t.lb > 0))
    .sort((a, b) => b[1].peak - a[1].peak);
  const agenda = [
    ...prep.upcoming.map((u) => ({ date: u.date, text: <>
      <b style={{ color: C.lotColors?.[u.lot] || C.brass }}>{u.lot}</b> {u.level} → {sign(u.sign)}
      <span style={{ color: C.dim }}> ({u.lord})</span>
      {u.peak && <b style={{ color: C.brass }}> · PEAK begins</b>}
      {u.lb && <b style={{ color: C.rose }}> · Loosing of the Bond</b>}</> })),
    ...returns.map((r) => ({ date: String(r.at).slice(0, 10), text: <>
      ⟲ {PLANET_GLYPH[r.body]} <b>{r.body} return</b>
      <span style={{ color: C.dim }}> exact {String(r.at).slice(11, 16)} UTC</span></> })),
    ...(prep.profection && prep.profection.end <= agendaHorizon(prep) ? [{
      date: prep.profection.end, text: <>🎂 Profection year turns —
        house {(prep.profection.house % 12) + 1} begins</> }] : []),
  ].sort((a, b) => a.date.localeCompare(b.date));

  const saveNotes = () => api.saveChartNotes(chartId, notes)
    .then(() => { setMsg("Notes saved."); setTimeout(() => setMsg(""), 2000); })
    .catch((e) => setMsg(e.message));

  return (
    <div style={{ maxWidth: print ? 720 : 860 }}>
      <style>{`@media print {
        header, .no-print { display: none !important; }
        body { background: #fff !important; }
        main { padding: 0 !important; }
        .print-break { page-break-before: always; }
        .avoid-break { break-inside: avoid; }
      }`}</style>

      <div className="no-print" style={{ display: "flex", gap: 10, alignItems: "baseline",
        marginBottom: 12 }}>
        <Link to="/intake-links" style={{ color: C.brass, fontSize: 12,
          textDecoration: "none" }}>← Client intake</Link>
        <span style={{ marginLeft: "auto" }} />
        {!print && <Link to={`/clients/${chartId}/print`} style={{ ...btn(false),
          textDecoration: "none" }}>Printable version</Link>}
        {print && <>
          <Link to={`/clients/${chartId}`} style={{ ...btn(false),
            textDecoration: "none" }}>Back to working view</Link>
          <button style={btn()} onClick={() => window.print()}>Print / save PDF</button>
        </>}
      </div>

      {/* header */}
      <div style={{ ...card, marginBottom: 12 }} className="avoid-break">
        <div style={{ display: "flex", gap: 14, alignItems: "baseline", flexWrap: "wrap" }}>
          <span style={{ fontFamily: C.displayFont, fontSize: 26 }}>{c.label}</span>
          <span style={{ fontSize: 12, color: C.dim }}>{c.place}</span>
          <span style={{ marginLeft: "auto", fontSize: 10.5, color: C.faint }}>
            prepared {prep.as_of} · next {prep.horizon_days} days</span>
        </div>
        <div style={{ fontSize: 13, marginTop: 6 }}>
          {c.asc_lon != null && `${Math.floor(c.asc_lon % 30)}° `}{c.asc_sign} rising
          {" · "}{sign(sOf(c.positions.Sun))} Sun · {sign(sOf(c.positions.Moon))} Moon
          {" · "}{c.sect} chart · profection house {prep.profection.house}
          {" ("}{prep.profection.profection_lord}{")"}
        </div>
      </div>

      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "flex-start",
        marginBottom: 12 }}>
        <div style={{ ...card, textAlign: "center" }} className="avoid-break">
          <ChartWheel positions={c.positions} lots={c.lots} asc={c.asc_sign}
            ascLon={c.asc_lon} size={print ? 300 : 330} showAspects={!print} />
        </div>
        {/* current releasing */}
        <div style={{ ...card, flexGrow: 1, minWidth: 260 }} className="avoid-break">
          <div style={label}>RELEASING TODAY</div>
          <table style={{ borderCollapse: "collapse", fontSize: 12.5, width: "100%" }}>
            <tbody>
              {Object.entries(prep.current).map(([lot, st]) => st && (
                <tr key={lot} style={{ borderTop: `1px solid ${C.line}` }}>
                  <td style={{ padding: "4px 6px", fontWeight: 600,
                    color: C.lotColors?.[lot] || C.text }}>{lot}</td>
                  {["l1", "l2"].map((l) => (
                    <td key={l} style={{ padding: "4px 6px" }}>
                      {st[l] ? <>
                        <span style={{ color: C.faint, fontSize: 10 }}>{l.toUpperCase()} </span>
                        {sign(st[l].sign)}
                        {st[l].peak && <b style={{ color: C.brass }}> ·</b>}
                        {st[l].lb && <b style={{ color: C.rose }}> ×</b>}
                      </> : "—"}
                    </td>))}
                </tr>))}
            </tbody>
          </table>
          <div style={{ fontSize: 10, color: C.faint, marginTop: 4 }}>
            · = peak period · × = Loosing of the Bond</div>
        </div>
      </div>

      {/* highlights */}
      {prep.events.length > 0 && (
        <div style={{ ...card, marginBottom: 12 }} className="avoid-break">
          <div style={label}>HIGHLIGHTS — {prep.events.length} REPORTED EVENT{prep.events.length === 1 ? "" : "S"}</div>
          {notable.length === 0 && <div style={{ fontSize: 12.5, color: C.dim }}>
            No events fell in peak periods or at a Loosing of the Bond.</div>}
          {notable.map(([lot, t]) => (
            <div key={lot} style={{ fontSize: 13, padding: "3px 0" }}>
              <b style={{ color: C.lotColors?.[lot] || C.text }}>{lot}</b>
              <span style={{ color: C.dim }}>
                {" — "}{t.peak > 0 && `${t.peak} of ${t.total} in peak periods`}
                {t.peak > 0 && t.lb > 0 && " · "}
                {t.lb > 0 && `${t.lb} at a Loosing of the Bond`}
              </span>
            </div>))}
        </div>
      )}

      {/* event timeline */}
      {prep.events.length > 0 && (
        <div style={{ ...card, marginBottom: 12 }}>
          <div style={label}>REPORTED EVENTS AGAINST THEIR PERIODS</div>
          {prep.events.map((e) => (
            <div key={e.id} className="avoid-break"
              style={{ borderTop: `1px solid ${C.line}`, padding: "7px 0" }}>
              <div style={{ fontSize: 12.5 }}>
                <span style={{ fontFamily: "ui-monospace,monospace", color: C.faint }}>
                  {e.date}</span>
                <b style={{ color: qc[e.quality] || C.text }}> {e.quality}</b>
                <span style={{ color: C.faint }}> · {e.life_area}
                  {e.blind && " · blind"}{e.provenance === "client_intake" && " · intake"}</span>
              </div>
              {e.note && <div style={{ fontSize: 12.5, color: C.text, margin: "2px 0" }}>
                {e.note}</div>}
              <div style={{ fontSize: 11, color: C.dim }}>
                {PREP_LOTS.map((lot) => e.marks[lot] && (
                  <span key={lot} style={{ marginRight: 12 }}>
                    <b style={{ color: C.lotColors?.[lot] || C.text }}>{lot}</b>
                    {" "}{e.marks[lot].l1?.sign}{e.marks[lot].l2 && `/${e.marks[lot].l2.sign}`}
                    {e.marks[lot].peak && <b style={{ color: C.brass }}> PEAK</b>}
                    {e.marks[lot].lb && <b style={{ color: C.rose }}> LB</b>}
                  </span>))}
                {e.profection_house && <span>house-{e.profection_house} year</span>}
              </div>
            </div>))}
        </div>
      )}

      {/* upcoming agenda */}
      <div style={{ ...card, marginBottom: 12 }} className={print ? "print-break" : ""}>
        <div style={label}>NEXT {prep.horizon_days} DAYS</div>
        {agenda.length === 0 && <div style={{ fontSize: 12.5, color: C.dim }}>
          No period boundaries or returns in this window — a steady stretch.</div>}
        {agenda.map((a, i) => (
          <div key={i} style={{ fontSize: 12.5, padding: "3px 0",
            borderTop: `1px solid ${C.line}` }}>
            <span style={{ fontFamily: "ui-monospace,monospace", color: C.faint,
              marginRight: 10 }}>{a.date}</span>{a.text}
          </div>))}
      </div>

      {/* notes */}
      <div style={{ ...card, marginBottom: 12 }} className="avoid-break">
        <div style={label}>ASTROLOGER NOTES</div>
        {print ? (
          (notes || "").split("\n").filter(Boolean).length > 0
            ? notes.split("\n").map((l, i) => (
                <p key={i} style={{ fontSize: 13, lineHeight: 1.7, margin: "0 0 6px" }}>{l}</p>))
            : <div style={{ fontSize: 12, color: C.faint }}>—</div>
        ) : (<>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={6}
            placeholder="Session prep, hypotheses to test, questions to ask…"
            style={{ ...input, width: "100%", boxSizing: "border-box", resize: "vertical",
              fontFamily: "inherit" }} />
          <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 8 }}>
            <button style={btn()} onClick={saveNotes}>Save notes</button>
            <span style={{ fontSize: 11.5, color: C.brass }}>{msg}</span>
            <span style={{ fontSize: 10.5, color: C.faint, marginLeft: "auto" }}>
              Encrypted, visible only to you. Printed on the handout.</span>
          </div>
        </>)}
      </div>

      {/* handwriting space, print only */}
      {print && (
        <div style={{ ...card }} className="avoid-break">
          <div style={label}>SESSION NOTES</div>
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} style={{ borderBottom: `1px solid ${C.line}`, height: 26 }} />))}
        </div>
      )}
    </div>
  );
}

const sOf = (lon) => ["Aries","Taurus","Gemini","Cancer","Leo","Virgo","Libra","Scorpio",
  "Sagittarius","Capricorn","Aquarius","Pisces"][Math.floor(((lon % 360) + 360) % 360 / 30)];
const agendaHorizon = (prep) => {
  const d = new Date(prep.as_of); d.setDate(d.getDate() + prep.horizon_days);
  return d.toISOString().slice(0, 10);
};
